// client/src/pages/ProfileEdit.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import request, { apiBaseUrl } from '../utils/request';
import { compressImageForUpload } from '../utils/imageCompress';
import { regionData } from '../data/regions';
import tagGroups from '../data/tags';

const HEIGHT_OPTIONS = Array.from({ length: 71 }, (_, i) => 140 + i);
const EDUCATION_OPTIONS = ['大专以下', '大专', '本科', '硕士', '博士'];
const SALARY_OPTIONS = ['5万以下', '5-10万', '10-20万', '20-30万', '30-60万', '60-100万', '100万以上'];
const ZODIAC_OPTIONS = ['白羊座', '金牛座', '双子座', '巨蟹座', '狮子座', '处女座', '天秤座', '天蝎座', '射手座', '摩羯座', '水瓶座', '双鱼座'];
const CAR_OPTIONS = ['暂无', '已购车'];
const MARRIAGE_OPTIONS = ['未婚', '离异', '丧偶'];
const DATING_GOAL_OPTIONS = ['计划1年内结婚', '计划2年内结婚', '时机成熟就结婚', '我就想要纯纯的恋爱'];
const MAX_PHOTOS = 3;

function ProfileEdit() {
  const [formData, setFormData] = useState({
    nickname: '',
    avatar: '',
    gender: '',
    birthday: '',
    age: '',
    height: '',
    education: '',
    school: '',
    job: '',
    annual_salary: '',
    hometown: '',
    hometownProvince: '',
    hometownCity: '',
    hometownDistrict: '',
    city: '',
    cityProvince: '',
    cityCity: '',
    cityDistrict: '',
    intro: '',
    ideal_type: '',
    zodiac: '',
    housing: '',
    housingProvince: '',
    housingCity: '',
    car: '',
    photos: [],
    marriage: '',
    dating_goal: '',
    wechat: '',
    tags: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const res = await request({ url: '/api/user/me', method: 'GET' });
      if (res.data) {
        const d = res.data;
        const photos = Array.isArray(d.photos) ? d.photos : [];
        const tags = Array.isArray(d.tags) ? d.tags : [];
        const [hp, hc, hd] = parseRegion(d.hometown);
        const [cp, cc, cd] = parseRegion(d.city);
        const housingVal = d.housing || '';
        const housingParts = housingVal.startsWith('有房-') ? housingVal.replace('有房-', '').split('-') : [];
        const hsp = housingParts[0] || '';
        const hsc = housingParts[1] || '';
        setFormData({
          nickname: d.nickname || '',
          avatar: d.avatar || '',
          gender: d.gender || '',
          birthday: d.birthday || '',
          age: d.age ? String(d.age) : '',
          height: d.height ? String(d.height) : '',
          education: d.education || '',
          school: d.school || '',
          job: d.job || '',
          annual_salary: d.annual_salary || '',
          hometown: d.hometown || '',
          hometownProvince: hp,
          hometownCity: hc,
          hometownDistrict: hd,
          city: d.city || '',
          cityProvince: cp,
          cityCity: cc,
          cityDistrict: cd,
          intro: d.intro || '',
          ideal_type: d.ideal_type || '',
          zodiac: d.zodiac || '',
          housing: housingVal,
          housingProvince: hsp,
          housingCity: hsc,
          car: d.car || '',
          photos,
          marriage: d.marriage || '',
          dating_goal: d.dating_goal || '',
          wechat: d.wechat || '',
          tags
        });
      }
    } catch (err) {
      console.log('新用户，无现有资料');
    }
  };

  function parseRegion(str) {
    if (!str) return ['', '', ''];
    const parts = str.split(/[\/\-]/);
    return [parts[0] || '', parts[1] || '', parts[2] || ''];
  }

  function calcAge(birthday) {
    if (!birthday) return '';
    const parts = birthday.split('-').map(Number);
    if (parts.length < 2) return '';
    const [y, m, d] = parts;
    if (!y || !m) return '';
    const now = new Date();
    let age = now.getFullYear() - y;
    if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < (d || 1))) age--;
    return age > 0 ? String(age) : '';
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'birthday') {
        next.age = calcAge(value);
      } else if (name === 'hometownProvince') {
        next.hometownCity = '';
        next.hometownDistrict = '';
        next.hometown = value ? `${value}` : '';
      } else if (name === 'hometownCity') {
        next.hometownDistrict = '';
        next.hometown = value ? `${next.hometownProvince}/${value}` : next.hometownProvince;
      } else if (name === 'hometownDistrict') {
        next.hometown = value ? `${next.hometownProvince}/${next.hometownCity}/${value}` : `${next.hometownProvince}/${next.hometownCity}`;
      } else if (name === 'cityProvince') {
        next.cityCity = '';
        next.cityDistrict = '';
        next.city = value ? `${value}` : '';
      } else if (name === 'cityCity') {
        next.cityDistrict = '';
        next.city = value ? `${next.cityProvince}/${value}` : next.cityProvince;
      } else if (name === 'cityDistrict') {
        next.city = value ? `${next.cityProvince}/${next.cityCity}/${value}` : `${next.cityProvince}/${next.cityCity}`;
      } else if (name === 'housing') {
        if (value !== '有房') {
          next.housingProvince = '';
          next.housingCity = '';
        }
      } else if (name === 'housingProvince') {
        next.housingCity = '';
        next.housing = value ? `有房-${value}` : '暂无';
      } else if (name === 'housingCity') {
        next.housing = next.housingProvince ? `有房-${next.housingProvince}-${value}` : '暂无';
      }
      return next;
    });
    const errs = { ...errors };
    if (errs[name]) errs[name] = '';
    if (['hometownProvince', 'hometownCity', 'hometownDistrict'].includes(name)) errs.hometown = '';
    if (['cityProvince', 'cityCity', 'cityDistrict'].includes(name)) errs.city = '';
    if (name === 'height') errs.height = '';
    if (name === 'education') errs.education = '';
    if (name === 'wechat') errs.wechat = '';
    setErrors(errs);
  };

  const MAX_TAGS = 8;
  const toggleTag = (tag) => {
    setFormData(prev => {
      const next = prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : prev.tags.length >= MAX_TAGS ? prev.tags : [...prev.tags, tag];
      return { ...prev, tags: next };
    });
    if (errors.tags) setErrors(prev => ({ ...prev, tags: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!(formData.nickname || '').trim()) newErrors.nickname = '昵称必填';
    else if ((formData.nickname || '').trim().length > 8) newErrors.nickname = '昵称最多8个字';
    if (formData.gender !== 'male' && formData.gender !== 'female') newErrors.gender = '请选择性别';
    if (!formData.birthday) newErrors.birthday = '出生年月日必填';
    if (!formData.height) newErrors.height = '身高必填';
    if (!formData.education) newErrors.education = '学历必填';
    if (!(formData.city || '').trim()) newErrors.city = '现居城市必填';
    if (!(formData.wechat || '').trim()) newErrors.wechat = '微信号必填';
    else if ((formData.wechat || '').length > 50) newErrors.wechat = '微信号最多50个字';
    if (formData.tags.length > 8) newErrors.tags = '兴趣标签最多选8个';
    if ((formData.school || '').length > 15) newErrors.school = '学校最多15个字';
    if ((formData.job || '').length > 10) newErrors.job = '职业最多10个字';
    if ((formData.intro || '').length > 300) newErrors.intro = '关于我最多300字';
    if ((formData.ideal_type || '').length > 100) newErrors.ideal_type = '我的理想型最多100字';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getPhotoSrc = (photo) => {
    if (typeof photo !== 'string') return '';
    return photo.startsWith('/uploads/') ? apiBaseUrl + photo : photo;
  };

  const [avatarDragOver, setAvatarDragOver] = useState(false);
  const [photosDragOver, setPhotosDragOver] = useState(false);
  const photosDragDepth = useRef(0);
  const photosRef = useRef([]);
  const avatarInputRef = useRef(null);
  const photosInputRef = useRef(null);
  useEffect(() => {
    photosRef.current = formData.photos;
  }, [formData.photos]);

  const uploadAvatarFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('请上传图片文件');
      return;
    }
    setLoading(true);
    try {
      const toUpload = await compressImageForUpload(file);
      const fd = new FormData();
      fd.append('photo', toUpload);
      const res = await request({ url: '/api/user/upload-photo', method: 'POST', data: fd });
      if (res.data?.url) setFormData(prev => ({ ...prev, avatar: res.data.url }));
    } catch (err) {
      const msg = err?.response?.data?.error ?? err?.message;
      alert(msg && String(msg).trim() ? msg : '上传失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadPhotoFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) {
      alert('请选择图片文件');
      return;
    }
    const remain = MAX_PHOTOS - photosRef.current.length;
    const validFiles = files.slice(0, remain);
    if (validFiles.length === 0) {
      alert(`最多只能上传 ${MAX_PHOTOS} 张照片`);
      return;
    }
    setLoading(true);
    try {
      const urls = [];
      for (const file of validFiles) {
        const toUpload = await compressImageForUpload(file);
        const fd = new FormData();
        fd.append('photo', toUpload);
        const res = await request({ url: '/api/user/upload-photo', method: 'POST', data: fd });
        if (res.data?.url) urls.push(res.data.url);
      }
      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...urls].slice(0, MAX_PHOTOS) }));
    } catch (err) {
      const msg = err?.response?.data?.error ?? err?.message;
      alert(msg && String(msg).trim() ? msg : '上传失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await uploadAvatarFile(file);
    e.target.value = '';
  };

  const handlePhotoChange = async (e) => {
    const files = e.target.files;
    if (files?.length) await uploadPhotoFiles(files);
    e.target.value = '';
  };

  const onAvatarDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types?.includes?.('Files')) setAvatarDragOver(true);
  };
  const onAvatarDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget;
    if (next && e.currentTarget.contains(next)) return;
    setAvatarDragOver(false);
  };
  const onAvatarDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onAvatarDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAvatarDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadAvatarFile(file);
  };

  const onPhotosDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    photosDragDepth.current += 1;
    setPhotosDragOver(true);
  };
  const onPhotosDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    photosDragDepth.current -= 1;
    if (photosDragDepth.current <= 0) {
      photosDragDepth.current = 0;
      setPhotosDragOver(false);
    }
  };
  const onPhotosDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onPhotosDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    photosDragDepth.current = 0;
    setPhotosDragOver(false);
    if (e.dataTransfer.files?.length) uploadPhotoFiles(e.dataTransfer.files);
  };

  const removePhoto = (i) => setFormData(prev => ({ ...prev, photos: prev.photos.filter((_, idx) => idx !== i) }));

  const handleSubmit = async () => {
    if (!validateForm()) {
      alert('请正确填写必填项');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        nickname: (formData.nickname || '').trim().slice(0, 8),
        avatar: formData.avatar || null,
        gender: (formData.gender === 'male' || formData.gender === 'female') ? formData.gender : null,
        birthday: formData.birthday || null,
        age: formData.age ? parseInt(formData.age, 10) : null,
        height: formData.height ? parseInt(formData.height, 10) : null,
        education: formData.education || null,
        school: (formData.school || '').slice(0, 15),
        job: (formData.job || '').slice(0, 10),
        annual_salary: formData.annual_salary || null,
        hometown: formData.hometown || null,
        city: formData.city || formData.hometown || null,
        intro: (formData.intro || '').slice(0, 300),
        ideal_type: (formData.ideal_type || '').trim().slice(0, 100) || null,
        zodiac: formData.zodiac || null,
        housing: formData.housing || (formData.housingProvince ? `有房-${formData.housingProvince}-${formData.housingCity}` : null),
        car: formData.car || null,
        photos: formData.photos,
        marriage: formData.marriage || null,
        dating_goal: formData.dating_goal || null,
        wechat: (formData.wechat || '').slice(0, 50),
        tags: formData.tags.slice(0, 8)
      };
      const res = await request({ url: '/api/user/profile', method: 'POST', data: payload });
      if (res.data?.success) {
        alert('✅ 资料保存成功！');
        navigate('/');
      }
    } catch (err) {
      alert(err.response?.data?.error || '保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 8, fontSize: 16 };
  const selectStyle = { ...inputStyle, backgroundColor: 'white', boxSizing: 'border-box', height: 48 };
  const labelStyle = { display: 'block', marginBottom: 8, fontWeight: 500 };

  return (
    <div className="container">
      <div style={{ padding: '20px 0' }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>← 返回</button>
        <h2 style={{ color: '#ff6b6b', marginBottom: 20 }}>📝 修改基本资料</h2>
        <div className="card">
          {/* 1. 昵称 - 必填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>昵称 <span style={{ color: 'red' }}>*</span></label>
            <input
              type="text"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              placeholder="最多8个字"
              maxLength={8}
              style={inputStyle}
            />
            {errors.nickname && <span style={{ color: 'red', fontSize: 12 }}>{errors.nickname}</span>}
          </div>

          {/* 2. 头像 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>头像 <span style={{ color: '#999', fontSize: 12 }}>默认微信头像，可更换</span></label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
                padding: 12,
                borderRadius: 12,
                border: avatarDragOver ? '2px dashed #ff6b6b' : '2px dashed #eee',
                background: avatarDragOver ? 'rgba(255,107,107,0.07)' : '#fafafa',
                transition: 'border 0.15s ease, background 0.15s ease',
              }}
              onDragEnter={onAvatarDragEnter}
              onDragLeave={onAvatarDragLeave}
              onDragOver={onAvatarDragOver}
              onDrop={onAvatarDrop}
            >
              <img
                src={(formData.avatar ? getPhotoSrc(formData.avatar) : null) || 'https://via.placeholder.com/80?text=%E5%BE%AE%E4%BF%A1%E5%A4%B4%E5%83%8F'}
                alt="头像"
                style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', pointerEvents: 'none' }}
              />
              <div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => avatarInputRef.current?.click()}
                >
                  选择图片
                </button>
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#999' }}>或将图片拖到左侧区域</p>
              </div>
            </div>
          </div>

          {/* 性别 - 必填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>性别 <span style={{ color: 'red' }}>*</span></label>
            <div style={{ display: 'flex', gap: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={handleChange} style={{ marginRight: 8 }} />男
              </label>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={handleChange} style={{ marginRight: 8 }} />女
              </label>
            </div>
            {errors.gender && <span style={{ color: 'red', fontSize: 12 }}>{errors.gender}</span>}
          </div>

          {/* 出生年月日 - 必填（包裹层保证与 select 同尺寸） */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>出生年月日 <span style={{ color: 'red' }}>*</span></label>
            <div style={{
              width: '100%', height: 48, border: '1px solid #ddd', borderRadius: 8, fontSize: 16,
              backgroundColor: 'white', boxSizing: 'border-box', position: 'relative'
            }}>
              <input
                type="date"
                name="birthday"
                value={formData.birthday}
                onChange={handleChange}
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  width: '100%', height: '100%',
                  border: 'none',
                  padding: '0 12px',
                  borderRadius: 8,
                  fontSize: 16,
                  backgroundColor: 'transparent',
                  outline: 'none'
                }}
              />
            </div>
            {formData.age && (
              <span style={{ color: '#666', fontSize: 13, marginTop: 6, display: 'inline-block' }}>
                当前年龄：{formData.age} 岁
              </span>
            )}
            {errors.birthday && <span style={{ color: 'red', fontSize: 12, display: 'block', marginTop: 4 }}>{errors.birthday}</span>}
          </div>

          {/* 3. 身高 - 必填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>身高 <span style={{ color: 'red' }}>*</span></label>
            <select name="height" value={formData.height} onChange={handleChange} style={selectStyle}>
              <option value="">请选择</option>
              {HEIGHT_OPTIONS.map(h => <option key={h} value={h}>{h}cm</option>)}
            </select>
            {errors.height && <span style={{ color: 'red', fontSize: 12 }}>{errors.height}</span>}
          </div>

          {/* 4. 学历 - 必填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>学历 <span style={{ color: 'red' }}>*</span></label>
            <select name="education" value={formData.education} onChange={handleChange} style={selectStyle}>
              <option value="">请选择</option>
              {EDUCATION_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            {errors.education && <span style={{ color: 'red', fontSize: 12 }}>{errors.education}</span>}
          </div>

          {/* 5. 学校 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>学校</label>
            <input
              type="text"
              name="school"
              value={formData.school}
              onChange={handleChange}
              placeholder="最多15个字"
              maxLength={15}
              style={inputStyle}
            />
            {errors.school && <span style={{ color: 'red', fontSize: 12 }}>{errors.school}</span>}
          </div>

          {/* 6. 职业 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>职业</label>
            <input
              type="text"
              name="job"
              value={formData.job}
              onChange={handleChange}
              placeholder="最多10个字"
              maxLength={10}
              style={inputStyle}
            />
            {errors.job && <span style={{ color: 'red', fontSize: 12 }}>{errors.job}</span>}
          </div>

          {/* 7. 年薪 - 选填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>年薪 <span style={{ color: '#999', fontSize: 12 }}>选填</span></label>
            <select name="annual_salary" value={formData.annual_salary} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
              <option value="">请选择</option>
              {SALARY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* 8. 家乡 - 级联（选填） */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>家乡 <span style={{ color: '#999', fontSize: 12 }}>选填</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <select name="hometownProvince" value={formData.hometownProvince} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                <option value="">省</option>
                {Object.keys(regionData).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select name="hometownCity" value={formData.hometownCity} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                <option value="">市</option>
                {formData.hometownProvince && Object.keys(regionData[formData.hometownProvince] || {}).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select name="hometownDistrict" value={formData.hometownDistrict} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                <option value="">县/区</option>
                {formData.hometownProvince && formData.hometownCity && (regionData[formData.hometownProvince]?.[formData.hometownCity] || []).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            {errors.hometown && <span style={{ color: 'red', fontSize: 12 }}>{errors.hometown}</span>}
          </div>

          {/* 9. 现居城市 - 必填 级联 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>现居城市 <span style={{ color: 'red' }}>*</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <select name="cityProvince" value={formData.cityProvince} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                <option value="">省</option>
                {Object.keys(regionData).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select name="cityCity" value={formData.cityCity} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                <option value="">市</option>
                {formData.cityProvince && Object.keys(regionData[formData.cityProvince] || {}).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select name="cityDistrict" value={formData.cityDistrict} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                <option value="">县/区</option>
                {formData.cityProvince && formData.cityCity && (regionData[formData.cityProvince]?.[formData.cityCity] || []).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            {errors.city && <span style={{ color: 'red', fontSize: 12 }}>{errors.city}</span>}
          </div>

          {/* 10. 关于我 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>关于我</label>
            <textarea
              name="intro"
              value={formData.intro}
              onChange={handleChange}
              placeholder="最多300字"
              rows={4}
              maxLength={300}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <span style={{ color: '#999', fontSize: 12 }}>{formData.intro.length}/300</span>
            {errors.intro && <span style={{ color: 'red', fontSize: 12 }}>{errors.intro}</span>}
          </div>

          {/* 10.5 我的理想型 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>我的理想型 <span style={{ color: '#999', fontSize: 12 }}>最多100字</span></label>
            <textarea
              name="ideal_type"
              value={formData.ideal_type}
              onChange={handleChange}
              placeholder="描述你心目中的理想另一半..."
              rows={3}
              maxLength={100}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <span style={{ color: '#999', fontSize: 12 }}>{formData.ideal_type.length}/100</span>
            {errors.ideal_type && <span style={{ color: 'red', fontSize: 12 }}>{errors.ideal_type}</span>}
          </div>

          {/* 11. 星座 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>星座</label>
            <select name="zodiac" value={formData.zodiac} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
              <option value="">请选择</option>
              {ZODIAC_OPTIONS.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>

          {/* 12. 房产 - 选填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>房产 <span style={{ color: '#999', fontSize: 12 }}>选填</span></label>
            <select name="housing" value={formData.housing === '暂无' || !formData.housing ? '暂无' : '有房'} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
              <option value="暂无">暂无</option>
              <option value="有房">有房</option>
            </select>
            {formData.housing && formData.housing !== '暂无' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                <select name="housingProvince" value={formData.housingProvince} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                  <option value="">省</option>
                  {Object.keys(regionData).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select name="housingCity" value={formData.housingCity} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
                  <option value="">市</option>
                  {formData.housingProvince && Object.keys(regionData[formData.housingProvince] || {}).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* 13. 车产 - 选填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>车产 <span style={{ color: '#999', fontSize: 12 }}>选填</span></label>
            <select name="car" value={formData.car} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
              <option value="">请选择</option>
              {CAR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* 14. 个人形象照 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>个人形象照 <span style={{ color: '#999', fontSize: 12 }}>3张</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {formData.photos.map((photo, i) => (
                <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '2px solid #ff6b6b', background: '#f5f5f5' }}>
                  <img src={getPhotoSrc(photo)} alt={`${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button type="button" onClick={() => removePhoto(i)} style={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: 'white', cursor: 'pointer' }}>×</button>
                </div>
              ))}
              {formData.photos.length < MAX_PHOTOS && (
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      photosInputRef.current?.click();
                    }
                  }}
                  style={{
                    aspectRatio: '1',
                    borderRadius: 8,
                    border: photosDragOver ? '2px dashed #ff6b6b' : '2px dashed #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    background: photosDragOver ? 'rgba(255,107,107,0.08)' : '#fafafa',
                    color: '#999',
                    padding: 8,
                    transition: 'border 0.15s ease, background 0.15s ease',
                  }}
                  onClick={() => photosInputRef.current?.click()}
                  onDragEnter={onPhotosDragEnter}
                  onDragLeave={onPhotosDragLeave}
                  onDragOver={onPhotosDragOver}
                  onDrop={onPhotosDrop}
                >
                  <input
                    ref={photosInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
                    <span style={{ fontSize: 15, display: 'block' }}>+ 添加照片</span>
                    <span style={{ fontSize: 11, color: '#aaa', lineHeight: 1.3, display: 'block', marginTop: 6 }}>点击或拖拽图片到此处</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 15. 婚姻状况 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>婚姻状况</label>
            <select name="marriage" value={formData.marriage} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
              <option value="">请选择</option>
              {MARRIAGE_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* 15.5 脱单目标 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>脱单目标</label>
            <select name="dating_goal" value={formData.dating_goal} onChange={handleChange} style={{ ...inputStyle, backgroundColor: 'white' }}>
              <option value="">请选择</option>
              {DATING_GOAL_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          {/* 16. 微信号 - 必填 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>微信号 <span style={{ color: 'red' }}>*</span></label>
            <input
              type="text"
              name="wechat"
              value={formData.wechat}
              onChange={handleChange}
              placeholder="最多50个字"
              maxLength={50}
              style={inputStyle}
            />
            {errors.wechat && <span style={{ color: 'red', fontSize: 12 }}>{errors.wechat}</span>}
          </div>

          {/* 17. 兴趣标签 - 最多选8个 */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>兴趣标签 <span style={{ color: '#999', fontSize: 12 }}>最多选8个</span></label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {errors.tags && <span style={{ color: 'red', fontSize: 12 }}>{errors.tags}</span>}
              <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>已选 {formData.tags.length}/8</span>
              {tagGroups.map(g => (
                <div key={g.label}>
                  <span style={{ fontSize: 13, color: '#666', marginBottom: 4, display: 'block' }}>{g.label}</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {g.tags.map(t => (
                      <span
                        key={t}
                        onClick={() => toggleTag(t)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && toggleTag(t)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 20,
                          fontSize: 13,
                          cursor: 'pointer',
                          border: '1px solid #ddd',
                          background: formData.tags.includes(t) ? '#ff6b6b' : '#f5f5f5',
                          color: formData.tags.includes(t) ? 'white' : '#333'
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ flex: 1 }}>取消</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading} style={{ flex: 2 }}>{loading ? '保存中...' : '保存资料'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileEdit;
